// src/lib/coverage/personalizationPercent.ts
// ============================================================
// THE PERSONALIZATION PERCENTAGE — one number, derived from measure coverage
//
// The intake cards carry a percentage again. It is NOT the old one.
//
// ─────────────────────────────────────────────────────────────────────────────
// WHAT THE OLD FORMULA GOT WRONG. `0.4·profile + 0.5·surveys + 0.1·documents` gave
// documents ten percent of the weight, which is backwards for a product whose whole
// credibility rests on having read the client's documents. It also measured card
// COMPLETION rather than what the completion produced: a client could reach 90% without
// one measure having a document behind it.
//
// This one weights each of the 38 measures by HOW WELL IT IS INFORMED, and averages:
//
//   evidence 1.0 · survey 0.6 · profile 0.3 · default 0
//   percent = round(100 x sum(weight per measure) / 38)
//
// Documents now move the number 21 points across the demo arc instead of 10, because
// moving a measure from survey to evidence is worth 0.4 of a measure and there is no
// separate documents component to cap it.
// ─────────────────────────────────────────────────────────────────────────────
//
// The denominator is the catalog size, taken off the aggregates rather than typed, so a
// measure added to the catalog dilutes the score instead of being silently ignored.
// ============================================================

import type { CoverageAggregates, Tier } from './computeCoverage';

/** How much an informed measure is worth, by the strength of what informed it. */
export const TIER_WEIGHTS: Readonly<Record<Tier, number>> = {
  evidence: 1.0,
  survey: 0.6,
  profile: 0.3,
  default: 0,
};

/** The percentage the intake surface renders. Whole number, 0 to 100. */
export function personalizationPercent(aggregates: CoverageAggregates): number {
  const weighted = (Object.keys(TIER_WEIGHTS) as Tier[]).reduce(
    (sum, tier) => sum + aggregates.counts[tier] * TIER_WEIGHTS[tier],
    0,
  );
  if (aggregates.total === 0) return 0;
  return Math.round((100 * weighted) / aggregates.total);
}

// ---- label bands -----------------------------------------------------------

/**
 * A SEPARATE BAND SCALE, and it has to be.
 *
 * src/lib/strengthScale is calibrated for the old composite, where completing the profile
 * and the surveys reached 90. Applied here it would label the opening state "Weak" at 39
 * and put every reachable state below "Strong", because this number is bounded by
 * something the old one was not: the 38-measure catalog.
 *
 * THE CEILING IS NOT 100, AND IT MOVES WHEN AN INSTRUMENT IS ADDED. Measures with no
 * instrument at all (see gapInstruments) cannot be informed by anything a client supplies.
 * With every reachable measure evidence-backed the score is 30/38, or 79.
 *
 * That number was 71 before D4 shipped: the governance charter reaches 37, 38 and 40,
 * which had been sitting on library default with nothing able to touch them. Excellent was
 * unreachable then and is reachable now, by exactly the client who fills in everything
 * asked of them. The bands are deliberately NOT retuned each time the ceiling moves, or a
 * client's score would fall when the product got better at asking.
 *
 * Kept out of strengthScale on purpose: that module has exactly one consumer left, the
 * Organization Profile sidebar, and Profile Strength is a different metric on a different
 * scale. Sharing the bands is what produced two numbers claiming to be the same thing.
 */
export interface PersonalizationBand {
  label: string;
  /** Tailwind text colour. */
  color: string;
  /** Tailwind background, for a chip. */
  bgColor: string;
  /** Tailwind background, for a meter fill. */
  barColor: string;
}

const BANDS: { min: number; band: PersonalizationBand }[] = [
  { min: 75, band: { label: 'Excellent', color: 'text-green-700', bgColor: 'bg-green-50', barColor: 'bg-green-500' } },
  { min: 60, band: { label: 'Strong', color: 'text-green-700', bgColor: 'bg-green-50', barColor: 'bg-green-500' } },
  { min: 45, band: { label: 'Moderate', color: 'text-[#18365E]', bgColor: 'bg-[#F5F7FA]', barColor: 'bg-[#18365E]' } },
  { min: 30, band: { label: 'Developing', color: 'text-[#FF6E4C]', bgColor: 'bg-[#FF6E4C]/10', barColor: 'bg-[#FF6E4C]' } },
  { min: 0, band: { label: 'Minimal', color: 'text-[#717182]', bgColor: 'bg-[#F5F7FA]', barColor: 'bg-[#e5e7eb]' } },
];

export function personalizationBand(percent: number): PersonalizationBand {
  return (BANDS.find((b) => percent >= b.min) ?? BANDS[BANDS.length - 1]).band;
}

/**
 * The highest score the current instrument set can reach, as a percentage.
 *
 * Exposed so a test can assert the bands are calibrated against it rather than against
 * 100, and so the number is findable when someone asks why the demo never hits Excellent.
 */
export function reachableCeiling(reachableMeasures: number, totalMeasures: number): number {
  return totalMeasures === 0 ? 0 : Math.round((100 * reachableMeasures) / totalMeasures);
}
